import { IonIcon } from '@ionic/react';
import {
  briefcaseOutline, shieldCheckmarkOutline,
  calendarOutline, timeOutline, globeOutline, chevronForward,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { useSessionInfo } from '@/lib/api/hooks';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const ROLE_LABELS: Record<string, string> = {
  candidate: 'Candidate',
  employer: 'Employer',
  both: 'Candidate & Employer',
};

const ROLE_COLORS: Record<string, { bg: string; fg: string }> = {
  candidate: { bg: 'rgba(59, 130, 246, 0.12)', fg: '#60a5fa' },
  employer: { bg: 'rgba(168, 85, 247, 0.12)', fg: '#c084fc' },
  both: { bg: 'rgba(16, 185, 129, 0.12)', fg: '#34d399' },
};

export function AccountInfo() {
  const history = useHistory();
  const profile = useAuthStore((s) => s.profile);
  const { data: sessionEnvelope } = useSessionInfo();
  const session = sessionEnvelope?.data;

  if (!profile) return null;

  const role = profile.role ?? 'candidate';
  const roleStyle = ROLE_COLORS[role] ?? ROLE_COLORS.candidate;

  return (
    <div className="cd-account-card">
      {/* Header row */}
      <div className="cd-account-header">
        <div className="cd-account-avatar">
          {profile.name?.charAt(0)?.toUpperCase() ?? '?'}
        </div>
        <div className="cd-account-identity">
          <h3 className="cd-account-name">{profile.name || 'Unnamed Account'}</h3>
          <span className="cd-account-email">{profile.email}</span>
        </div>
        <button
          type="button"
          className="cd-account-edit-btn"
          onClick={() => history.push('/profile/edit')}
          aria-label="Edit profile"
        >
          Edit
        </button>
      </div>

      {/* Info grid */}
      <div className="cd-account-grid">
        <InfoRow
          icon={briefcaseOutline}
          label="Role"
          value={
            <span
              className="cd-account-badge"
              style={{ background: roleStyle.bg, color: roleStyle.fg }}
            >
              {ROLE_LABELS[role] ?? role}
            </span>
          }
        />
        {session && (
          <>
            <InfoRow
              icon={calendarOutline}
              label="Member since"
              value={formatDate(session.issuedAt)}
            />
            <InfoRow
              icon={timeOutline}
              label="Last active"
              value={formatRelative(session.lastActiveAt)}
            />
            <InfoRow
              icon={shieldCheckmarkOutline}
              label="Session"
              value={
                <span style={{ color: session.isActive ? 'var(--bp-success, #22c55e)' : 'var(--bp-error, #ef4444)' }}>
                  {session.isActive ? 'Active' : 'Expired'}
                </span>
              }
            />
            {session.currentTenant?.name && (
              <InfoRow
                icon={globeOutline}
                label="Tenant"
                value={session.currentTenant.name}
              />
            )}
          </>
        )}
      </div>

      {/* Quick links */}
      <div className="cd-account-links">
        <QuickLink label="Edit profile" onClick={() => history.push('/profile/edit')} />
        <QuickLink label="Manage skills" onClick={() => history.push('/profile/skills')} />
        <QuickLink label="Work experience" onClick={() => history.push('/profile/experience')} />
        <QuickLink label="Verify identity" onClick={() => history.push('/profile/verify-identity')} />
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: React.ReactNode }) {
  return (
    <div className="cd-account-row">
      <IonIcon icon={icon} className="cd-account-row-icon" />
      <span className="cd-account-row-label">{label}</span>
      <span className="cd-account-row-value">{value}</span>
    </div>
  );
}

function QuickLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="cd-account-link" onClick={onClick}>
      <span>{label}</span>
      <IonIcon icon={chevronForward} />
    </button>
  );
}
